import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/context/AuthContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { WishlistProvider } from "@/context/WishlistContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/ErrorBoundary";
import { useEffect } from "react";
import { useAnalytics } from "@/lib/useAnalytics";

import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import WhatsAppFab from "@/components/layout/WhatsAppFab";
import TickerBar from "@/components/TickerBar";
import ProtectedRoute from "@/components/ProtectedRoute";

import Home from "@/pages/Home";
import Search from "@/pages/Search";
import CategoryLanding from "@/pages/CategoryLanding";
import ProductDetail from "@/pages/ProductDetail";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Checkout from "@/pages/Checkout";
import MyBookings from "@/pages/MyBookings";
import Wishlist from "@/pages/Wishlist";
import Profile from "@/pages/Profile";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import FAQ from "@/pages/FAQ";
import AdminBlog from "@/pages/AdminBlog";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminProducts from "@/pages/AdminProducts";
import AdminBookings from "@/pages/AdminBookings";
import AdminSettings from "@/pages/AdminSettings";
import AdminFAQ from "@/pages/AdminFAQ";
import AdminTickers from "@/pages/AdminTickers";
import AdminEnquiries from "@/pages/AdminEnquiries";
import AdminPages from "@/pages/AdminPages";
import AdminPageBuilder from "@/pages/AdminPageBuilder";
import AdminApprovals from "@/pages/AdminApprovals";
import AdminBanners from "@/pages/AdminBanners";
import AdminItineraryBuilder from "@/pages/AdminItineraryBuilder";
import AdminTeam from "@/pages/AdminTeam";
import CMSPage from "@/pages/CMSPage";
import VendorDashboard from "@/pages/VendorDashboard";
import NotFound from "@/pages/NotFound";
import Enquire from "@/pages/Enquire";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AnalyticsTracker() {
  useAnalytics();
  return null;
}

function Shell({ children }) {
  const { pathname } = useLocation();
  const hideChrome = pathname === "/login" || pathname === "/register";
  return (
    <>
      {!hideChrome && <TickerBar />}
      {!hideChrome && <Header />}
      <main>{children}</main>
      {!hideChrome && <Footer />}
      {!hideChrome && <WhatsAppFab />}
    </>
  );
}

function App() {
  useEffect(() => {
    if (!document.querySelector('script[src*="checkout.razorpay.com"]')) {
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.async = true;
      document.body.appendChild(s);
    }
  }, []);

  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <ThemeProvider>
            <SettingsProvider>
              <AuthProvider>
                <WishlistProvider>
                  <ScrollToTop />
                  <AnalyticsTracker />
                  <Shell>
                    <ErrorBoundary>
                      <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/search" element={<Search />} />
                        <Route path="/tours" element={<CategoryLanding categoryKey="tour" />} />
                        <Route path="/hotels" element={<CategoryLanding categoryKey="hotel" />} />
                        <Route path="/tickets" element={<CategoryLanding categoryKey="ticket" />} />
                        <Route path="/cabs" element={<CategoryLanding categoryKey="cab" />} />
                        <Route path="/adventure" element={<CategoryLanding categoryKey="activity" />} />
                        <Route path="/international" element={<CategoryLanding categoryKey="international" />} />
                        {/* Removed: /product/:slug route — use /:slug for SEO-friendly URLs */}
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/checkout" element={<Checkout />} />
                        <Route path="/my-bookings" element={<ProtectedRoute><MyBookings /></ProtectedRoute>} />
                        <Route path="/wishlist" element={<ProtectedRoute><Wishlist /></ProtectedRoute>} />
                        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                        <Route path="/blog" element={<Blog />} />
                        <Route path="/blog/:slug" element={<BlogPost />} />
                        <Route path="/faq" element={<FAQ />} />
                        <Route path="/enquire" element={<Enquire />} />
                        <Route path="/p/:slug" element={<CMSPage />} />
                        <Route path="/admin" element={<ProtectedRoute roles={["super_admin", "admin", "manager", "user"]}><AdminDashboard /></ProtectedRoute>} />
                        <Route path="/admin/products" element={<ProtectedRoute roles={["super_admin", "admin", "manager", "user"]}><AdminProducts /></ProtectedRoute>} />
                        <Route path="/admin/bookings" element={<ProtectedRoute roles={["super_admin", "admin", "manager", "user"]}><AdminBookings /></ProtectedRoute>} />
                        <Route path="/admin/blog" element={<ProtectedRoute roles={["super_admin", "admin", "manager", "user"]}><AdminBlog /></ProtectedRoute>} />
                        <Route path="/admin/settings" element={<ProtectedRoute roles={["super_admin", "admin"]}><AdminSettings /></ProtectedRoute>} />
                        <Route path="/admin/faq" element={<ProtectedRoute roles={["super_admin", "admin", "manager"]}><AdminFAQ /></ProtectedRoute>} />
                        <Route path="/admin/tickers" element={<ProtectedRoute roles={["super_admin", "admin", "manager"]}><AdminTickers /></ProtectedRoute>} />
                        <Route path="/admin/enquiries" element={<ProtectedRoute roles={["super_admin", "admin", "manager", "user"]}><AdminEnquiries /></ProtectedRoute>} />
                        <Route path="/admin/pages" element={<ProtectedRoute roles={["super_admin", "admin", "manager"]}><AdminPages /></ProtectedRoute>} />
                        <Route path="/admin/pages/:pageId" element={<ProtectedRoute roles={["super_admin", "admin", "manager"]}><AdminPageBuilder /></ProtectedRoute>} />
                        <Route path="/admin/approvals" element={<ProtectedRoute roles={["super_admin", "admin", "manager"]}><AdminApprovals /></ProtectedRoute>} />
                        <Route path="/admin/banners" element={<ProtectedRoute roles={["super_admin", "admin", "manager"]}><AdminBanners /></ProtectedRoute>} />
                        <Route path="/admin/team" element={<ProtectedRoute roles={["super_admin", "admin"]}><AdminTeam /></ProtectedRoute>} />
                        <Route path="/admin/itinerary/:productId" element={<ProtectedRoute roles={["super_admin", "admin", "manager", "vendor"]}><AdminItineraryBuilder /></ProtectedRoute>} />
                        <Route path="/vendor" element={<ProtectedRoute roles={["vendor"]}><VendorDashboard /></ProtectedRoute>} />
                        {/* SEO-friendly product URLs: /:slug (must be last before catch-all) */}
                        <Route path="/:slug" element={<ProductDetail />} />
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </ErrorBoundary>
                  </Shell>
                  <Toaster position="top-right" richColors />
                </WishlistProvider>
              </AuthProvider>
            </SettingsProvider>
          </ThemeProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
